<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  externalChannels: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:externalChannels']);

const defaultChannels = [
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: 'i-lucide-phone',
    placeholder: 'https://wa.me/YOURNUMBER',
  },
  {
    id: 'messenger',
    name: 'Facebook Messenger',
    icon: 'i-lucide-facebook',
    placeholder: 'https://m.me/YOURPAGE',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: 'i-lucide-instagram',
    placeholder: 'https://ig.me/m/YOURUSERNAME',
  },
  {
    id: 'telegram',
    name: 'Telegram',
    icon: 'i-lucide-send',
    placeholder: 'https://t.me/YOURUSERNAME',
  },
];

const localChannels = ref({ ...props.externalChannels });

watch(
  () => props.externalChannels,
  newVal => {
    localChannels.value = { ...newVal };
  },
  { deep: true }
);

const emitUpdate = () => {
  emit('update:externalChannels', { ...localChannels.value });
};

const isChannelEnabled = channelId => {
  return localChannels.value[channelId]?.enabled || false;
};

const getChannelUrl = channelId => {
  return localChannels.value[channelId]?.url || '';
};

const toggleChannel = channelId => {
  if (!localChannels.value[channelId]) {
    localChannels.value[channelId] = { enabled: false, url: '' };
  }
  localChannels.value[channelId].enabled =
    !localChannels.value[channelId].enabled;
  emitUpdate();
};

const updateChannelUrl = (channelId, url) => {
  if (!localChannels.value[channelId]) {
    localChannels.value[channelId] = { enabled: false, url: '' };
  }
  localChannels.value[channelId].url = url;
  emitUpdate();
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <h3 class="text-base font-semibold text-n-slate-12">
      {{ $t('INBOX_MGMT.WIDGET_BUILDER.EXTERNAL_CHANNELS.TITLE') }}
    </h3>
    <p class="text-sm text-n-slate-11 -mt-2">
      {{ $t('INBOX_MGMT.WIDGET_BUILDER.EXTERNAL_CHANNELS.DESCRIPTION') }}
    </p>

    <div
      v-for="channel in defaultChannels"
      :key="channel.id"
      class="flex flex-col gap-2 p-4 border border-n-weak rounded-lg"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <i :class="channel.icon" class="size-5 text-n-slate-11" />
          <span class="font-medium text-n-slate-12">{{ channel.name }}</span>
        </div>
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            class="sr-only peer"
            :checked="isChannelEnabled(channel.id)"
            @change="toggleChannel(channel.id)"
          />
          <div
            class="w-11 h-6 bg-n-slate-5 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
          />
        </label>
      </div>

      <div v-if="isChannelEnabled(channel.id)" class="mt-2">
        <woot-input
          :model-value="getChannelUrl(channel.id)"
          :label="$t('INBOX_MGMT.WIDGET_BUILDER.EXTERNAL_CHANNELS.URL_LABEL')"
          :placeholder="channel.placeholder"
          @update:model-value="updateChannelUrl(channel.id, $event)"
        />
      </div>
    </div>
  </div>
</template>
